import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { AI_WORKSPACE_QUERY_KEYS } from "../constants";
import { AiWorkspaceService } from "../services/aiWorkspace.service";
import { IAIWorkspaceRequest, IScriptGenerateCreator } from "../types";

const FIT_DONE = new Set(["completed", "failed", "success", "error", "cancelled", "canceled"]);
const SCRIPT_RESTING = new Set([
  "waiting_user_inputs",
  "conceptualised",
  "ready",
  "failed",
]);

function fitCheckInProgress(
  check: NonNullable<IAIWorkspaceRequest["creatorFitChecks"]>[number]
) {
  const job = check.briefFitEvaluationJob;
  if (!job) return Boolean(check.briefFitEvaluationJobId);
  const status = String(job.status || "").toLowerCase();
  if (!status) return true;
  return !FIT_DONE.has(status);
}

function scriptJobInProgress(item: IScriptGenerateCreator) {
  const job = item.creatorScriptGeneratorJob;
  if (!job) return Boolean(item.creatorScriptGeneratorJobId);
  const status = String(job.status || "").toLowerCase();
  if (!status) return true;
  return !SCRIPT_RESTING.has(status);
}

export function useGetAiWorkspaceRequest(aiWorkspaceRequestId: string) {
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: [AI_WORKSPACE_QUERY_KEYS.REQUEST, aiWorkspaceRequestId],
    queryFn: () =>
      AiWorkspaceService.getAIWorkspaceRequest(aiWorkspaceRequestId),
    enabled: !!aiWorkspaceRequestId,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const request = (
        query.state.data as BackednResponse<IAIWorkspaceRequest> | undefined
      )?.data;
      if (!request) return false;
      const scriptJobs =
        request.type === "script-generate"
          ? request.creatorScriptGenerations
          : undefined;
      const busy =
        request.creatorFitChecks?.some(fitCheckInProgress) ||
        scriptJobs?.some(scriptJobInProgress);
      return busy ? 5000 : false;
    },
  });

  const errorMessage = isError && error?.message;

  useEffect(() => {
    if (errorMessage) {
      ToastService.error(errorMessage);
    }
  }, [errorMessage]);

  return {
    data,
    isFetching: isLoading || isFetching,
    isLoading,
    isError,
    refetch,
  };
}
